import React, { useEffect, useMemo, useState } from 'react';
import ChatBubble from '@/features/chat/components/ChatBubble';
import type { ChatMessage } from '@/features/chat/types/chat';
import {
  listAgentTestSuites,
  getAgentTestSuite,
  runAgentTestSuite,
  type AgentTestRunnerMode,
  type AgentTestSuite,
  type AgentTestSuiteSummary,
  type AgentTestRunResult,
} from '@/services/agentTestsClient';

function formatMs(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m ${r}s`;
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function baseCaseId(caseId: string): string {
  return String(caseId || '').split('@')[0];
}

export default function ChatTestRunnerPage(): React.JSX.Element {
  const [suites, setSuites] = useState<AgentTestSuiteSummary[]>([]);
  const [selectedSuiteId, setSelectedSuiteId] = useState<string>('smoke');
  const [suite, setSuite] = useState<AgentTestSuite | null>(null);
  const [suitePath, setSuitePath] = useState<string | null>(null);

  const [mode, setMode] = useState<AgentTestRunnerMode>('inprocess');
  const [allowSideEffects, setAllowSideEffects] = useState(false);
  const [activeCaseResultId, setActiveCaseResultId] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<AgentTestRunResult | null>(null);

  const [loadingSuites, setLoadingSuites] = useState(false);
  const [loadingSuite, setLoadingSuite] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  // Load suite index (left panel dropdown)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingSuites(true);
        setError(null);
        const resp = await listAgentTestSuites();
        if (cancelled) return;
        setSuites(resp.suites || []);
        // Keep user selection if available; otherwise pick first suite.
        if (resp.suites?.length && !resp.suites.some((s) => s.suiteId === selectedSuiteId)) {
          setSelectedSuiteId(resp.suites[0].suiteId);
        }
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to load suites.');
      } finally {
        if (!cancelled) setLoadingSuites(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load full suite definition (so we can show the actual prompts/steps)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedSuiteId) return;
      try {
        setLoadingSuite(true);
        setError(null);
        setSuite(null);
        setSuitePath(null);
        setRunResult(null);
        setActiveCaseResultId(null);
        const resp = await getAgentTestSuite(selectedSuiteId);
        if (cancelled) return;
        setSuite(resp.suite);
        setSuitePath(resp.suitePath);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to load suite.');
      } finally {
        if (!cancelled) setLoadingSuite(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSuiteId]);

  const caseResultsByBaseId = useMemo(() => {
    const map = new Map<string, AgentTestRunResult['caseResults']>();
    if (!runResult) return map;
    for (const cr of runResult.caseResults || []) {
      const base = baseCaseId(cr.caseId);
      const arr = map.get(base) || [];
      arr.push(cr);
      map.set(base, arr);
    }
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => a.caseId.localeCompare(b.caseId));
      map.set(k, arr);
    }
    return map;
  }, [runResult]);

  const activeCaseResult = useMemo(() => {
    if (!runResult || !activeCaseResultId) return null;
    return runResult.caseResults.find((c) => c.caseId === activeCaseResultId) || null;
  }, [runResult, activeCaseResultId]);

  const activeCaseDefinition = useMemo(() => {
    if (!suite || !activeCaseResultId) return null;
    const baseId = baseCaseId(activeCaseResultId);
    return suite.cases.find((c) => c.caseId === baseId) || null;
  }, [suite, activeCaseResultId]);

  const transcript = useMemo(() => {
    if (!activeCaseResult) return [];
    const msgs: ChatMessage[] = [];

    for (const step of activeCaseResult.stepResults || []) {
      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: step.user,
        createdAt: new Date(step.startedAt),
        updatedAt: new Date(step.startedAt),
        status: 'delivered',
        renderType: 'text',
        metadata: {
          agentTest: true,
          caseId: activeCaseResult.caseId,
          mode: step.mode,
          stepIndex: step.stepIndex,
          stepPassed: step.passed,
        },
      };
      msgs.push(userMsg);

      const body = step.response?.body || {};
      const responseText =
        typeof body.response === 'string'
          ? body.response
          : typeof body === 'string'
            ? body
            : safeJson(body?.response ?? body);
      const renderType = body.renderHint === 'markdown' ? 'markdown' : 'text';

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: responseText || '(no response)',
        createdAt: new Date(step.finishedAt),
        updatedAt: new Date(step.finishedAt),
        status: 'delivered',
        renderType,
        metadata: {
          agentTest: true,
          caseId: activeCaseResult.caseId,
          mode: step.mode,
          stepIndex: step.stepIndex,
          stepPassed: step.passed,
          statusCode: step.response?.statusCode,
          intent: body.intent || body?.metadata?.intent,
        },
      };
      msgs.push(assistantMsg);
    }

    return msgs;
  }, [activeCaseResult]);

  const run = async (caseFilter?: string[]) => {
    if (!suite) return;
    try {
      setRunning(true);
      setError(null);
      const result = await runAgentTestSuite({
        suiteId: suite.suiteId,
        mode,
        allowSideEffects,
        caseFilter,
        defaultDeviceId: `web-test-runner-${mode}`,
      });

      setRunResult(result);

      // Select the first failing case, otherwise first case.
      const firstFail = (result.caseResults || []).find((c) => !c.passed);
      const first = (result.caseResults || [])[0];
      setActiveCaseResultId((firstFail || first)?.caseId || null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Test run failed.');
    } finally {
      setRunning(false);
    }
  };

  const selectedSuiteLabel = suite ? `${suite.suiteId}` : selectedSuiteId || '—';

  return (
    <div className="h-full min-h-0 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-[280px]">
          <h1 className="text-xl font-semibold text-[var(--brand-text-primary)]">Lex Chat Test Runner</h1>
          <p className="text-sm text-[var(--brand-text-secondary)]">
            Click a test prompt to run it, or run the whole suite. Results are logged with `agent_test.*`.
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm text-[var(--brand-text-secondary)]">Suite</label>
            <select
              className="px-3 py-2 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] text-sm"
              value={selectedSuiteId}
              onChange={(e) => setSelectedSuiteId(e.target.value)}
              disabled={loadingSuites || running}
            >
              {suites.length === 0 && <option value={selectedSuiteId}>{selectedSuiteId || 'loading…'}</option>}
              {suites.map((s) => (
                <option key={s.suiteId} value={s.suiteId}>
                  {s.suiteId}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-[var(--brand-text-secondary)]">Mode</label>
            <select
              className="px-3 py-2 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-card)] text-sm"
              value={mode}
              onChange={(e) => setMode(e.target.value as AgentTestRunnerMode)}
              disabled={running}
            >
              <option value="inprocess">inprocess (fast)</option>
              <option value="http">http (real wiring)</option>
              <option value="both">both</option>
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-[var(--brand-text-secondary)] select-none">
            <input
              type="checkbox"
              checked={allowSideEffects}
              onChange={(e) => setAllowSideEffects(e.target.checked)}
              disabled={running}
            />
            Allow side effects
          </label>

          <button
            className="px-4 py-2 rounded-lg bg-[var(--brand-accent-primary)] text-white text-sm font-medium disabled:opacity-50"
            onClick={() => run()}
            disabled={!suite || running || loadingSuite}
            title={!suite ? 'Load a suite first' : 'Run full suite'}
          >
            {running ? 'Running…' : 'Run Suite'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        {/* Left: suite + case library */}
        <div className="col-span-12 lg:col-span-4 min-h-0">
          <div className="h-full min-h-0 rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-[var(--brand-border)]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-[var(--brand-text-primary)]">
                    Suite: {selectedSuiteLabel}
                  </div>
                  <div className="text-xs text-[var(--brand-text-secondary)] truncate">
                    {suitePath || (loadingSuite ? 'Loading suite…' : '')}
                  </div>
                </div>
                {runResult && (
                  <div className="text-xs text-[var(--brand-text-secondary)] whitespace-nowrap">
                    {runResult.totals.passed}/{runResult.totals.cases} passed • {formatMs(runResult.durationMs)}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
              {loadingSuite && (
                <div className="p-4 text-sm text-[var(--brand-text-secondary)]">Loading…</div>
              )}

              {!loadingSuite && suite && suite.cases.length === 0 && (
                <div className="p-4 text-sm text-[var(--brand-text-secondary)]">No cases in this suite.</div>
              )}

              {!loadingSuite && suite && suite.cases.map((c) => {
                const baseId = c.caseId;
                const resultVariants = caseResultsByBaseId.get(baseId) || [];
                const isSelectedBase = activeCaseResultId ? baseCaseId(activeCaseResultId) === baseId : false;

                return (
                  <div
                    key={c.caseId}
                    className={`px-4 py-3 border-b border-[var(--brand-border)] hover:bg-[var(--brand-surface-hover)] transition-colors ${
                      isSelectedBase ? 'bg-[var(--brand-surface-muted)]' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <button
                        className="text-left min-w-0 flex-1"
                        onClick={() => {
                          if (resultVariants.length > 0) {
                            setActiveCaseResultId(resultVariants[0].caseId);
                          } else {
                            // Select base; transcript will show once run.
                            setActiveCaseResultId(c.caseId);
                          }
                        }}
                        disabled={running}
                        title={c.description || c.caseId}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="text-sm font-semibold text-[var(--brand-text-primary)] truncate">
                            {c.caseId}
                          </div>
                          {c.tags?.slice(0, 3).map((t) => (
                            <span
                              key={t}
                              className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--brand-surface-muted)] text-[var(--brand-text-secondary)] border border-[var(--brand-border)]"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                        {c.description && (
                          <div className="text-xs text-[var(--brand-text-secondary)] mt-1">{c.description}</div>
                        )}
                        {c.steps?.[0]?.user && (
                          <div className="text-xs text-[var(--brand-text-tertiary)] mt-2 line-clamp-2">
                            Prompt: {c.steps[0].user}
                          </div>
                        )}
                      </button>

                      <div className="flex flex-col items-end gap-2">
                        <button
                          className="px-3 py-1.5 rounded-lg text-xs font-medium border border-[var(--brand-border)] bg-[var(--brand-surface-card)] hover:bg-[var(--brand-surface-hover)] disabled:opacity-50"
                          onClick={() => run([c.caseId])}
                          disabled={running}
                          title="Run just this case"
                        >
                          Run
                        </button>

                        {resultVariants.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap justify-end">
                            {resultVariants.map((rv) => {
                              const isSelected = activeCaseResultId === rv.caseId;
                              const ok = rv.passed;
                              return (
                                <button
                                  key={rv.caseId}
                                  className={`px-2 py-1 rounded-md text-[10px] border ${
                                    isSelected ? 'border-[var(--brand-accent-primary)]' : 'border-[var(--brand-border)]'
                                  } ${ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
                                  onClick={() => setActiveCaseResultId(rv.caseId)}
                                  disabled={running}
                                  title={rv.caseId}
                                >
                                  {rv.caseId.includes('@') ? rv.caseId.split('@')[1] : 'result'}:{ok ? 'pass' : 'fail'}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Center: transcript */}
        <div className="col-span-12 lg:col-span-5 min-h-0">
          <div className="h-full min-h-0 rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-[var(--brand-border)]">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[var(--brand-text-primary)] truncate">
                    Transcript
                  </div>
                  <div className="text-xs text-[var(--brand-text-secondary)] truncate">
                    {activeCaseDefinition?.caseId
                      ? `${activeCaseDefinition.caseId}${activeCaseResult ? ` • ${activeCaseResult.passed ? 'pass' : 'fail'}` : ''}`
                      : 'Select a case to view results.'}
                  </div>
                </div>
                {activeCaseResult && (
                  <div className="text-xs text-[var(--brand-text-secondary)] whitespace-nowrap">
                    {formatMs(activeCaseResult.durationMs)}
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              {!activeCaseResult && (
                <div className="text-sm text-[var(--brand-text-secondary)]">
                  Run a case to populate a transcript.
                </div>
              )}

              {activeCaseResult && transcript.length === 0 && (
                <div className="text-sm text-[var(--brand-text-secondary)]">
                  No steps recorded for this case.
                </div>
              )}

              {activeCaseResult && transcript.map((m) => {
                const meta = (m.metadata || {}) as any;
                const showMetaLine = m.role === 'assistant';
                const metaLine = showMetaLine
                  ? `intent=${meta.intent || 'n/a'} • status=${meta.statusCode ?? 'n/a'} • mode=${meta.mode}`
                  : `mode=${meta.mode}`;

                const stepPassed = Boolean(meta.stepPassed);
                const metaColor = stepPassed ? 'text-[var(--brand-text-tertiary)]' : 'text-red-700';

                return (
                  <div key={m.id}>
                    <div className={`text-[11px] mb-1 ${metaColor}`}>{metaLine}</div>
                    <ChatBubble message={m} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: assertions + debug */}
        <div className="col-span-12 lg:col-span-3 min-h-0">
          <div className="h-full min-h-0 rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-card)] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-[var(--brand-border)] flex items-center justify-between gap-2">
              <div className="text-sm font-medium text-[var(--brand-text-primary)]">Assertions</div>
              <button
                className="text-xs px-2 py-1 rounded-md border border-[var(--brand-border)] bg-[var(--brand-surface-card)] hover:bg-[var(--brand-surface-hover)]"
                onClick={() => setShowRaw((v) => !v)}
                disabled={!activeCaseResult}
                title="Toggle raw response JSON"
              >
                {showRaw ? 'Hide raw' : 'Show raw'}
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              {!activeCaseResult && (
                <div className="text-sm text-[var(--brand-text-secondary)]">No results selected.</div>
              )}

              {activeCaseResult && (
                <div className="space-y-4">
                  <div className="text-xs text-[var(--brand-text-secondary)]">
                    {activeCaseResult.caseId} • {activeCaseResult.passed ? 'PASS' : 'FAIL'}
                  </div>

                  {(activeCaseResult.stepResults || []).map((s) => (
                    <div
                      key={`${activeCaseResult.caseId}:${s.stepIndex}`}
                      className={`rounded-lg border p-3 ${
                        s.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold">
                          Step {s.stepIndex} • {s.mode}
                        </div>
                        <div className="text-[11px] text-[var(--brand-text-secondary)]">
                          {formatMs(s.durationMs)}
                        </div>
                      </div>
                      {!s.passed && s.errors?.length > 0 && (
                        <ul className="mt-2 text-[11px] text-red-800 list-disc list-inside space-y-1">
                          {s.errors.map((err, idx) => (
                            <li key={idx}>{err}</li>
                          ))}
                        </ul>
                      )}
                      {s.passed && (
                        <div className="mt-2 text-[11px] text-green-800">All expectations satisfied.</div>
                      )}

                      {showRaw && (
                        <pre className="mt-3 text-[10px] whitespace-pre-wrap break-words bg-white/70 border border-[var(--brand-border)] rounded-md p-2 max-h-48 overflow-y-auto">
                          {safeJson(s.response)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
